import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DollarSign, PieChart, Lightbulb, AlertTriangle } from 'lucide-react'
import type { KpiData, UnitStatus, RevenueStreamId } from '@/lib/reports/space-yield/types'
import { formatCurrency } from '@/lib/reports/space-yield/compute'

interface SpaceYieldKpiCardsProps {
  data: KpiData
  isMtd: boolean
  activeStatus?: UnitStatus
  onStatusClick: (status: UnitStatus | undefined) => void
  streams?: RevenueStreamId[]
}

export function SpaceYieldKpiCards({ data, isMtd, activeStatus, onStatusClick, streams }: SpaceYieldKpiCardsProps) {
  const showLease = !streams || streams.includes('lease')
  const showTrans = !streams || streams.includes('transient')
  const showFuel  = !streams || streams.includes('fuel')

  // Total yield adjusts to only include enabled streams
  const displayedYield =
    (showLease ? data.lease_revenue : 0) +
    (showTrans ? data.transient_revenue : 0) +
    (showFuel ? data.fuel_revenue : 0)

  // Build breakdown parts
  const breakdownParts: string[] = []
  if (showLease) breakdownParts.push(`Lease: ${formatCurrency(data.lease_revenue)}`)
  if (showTrans) breakdownParts.push(`Trans: ${formatCurrency(data.transient_revenue)}`)

  // Title reflects what's included
  const yieldTitle = showFuel && data.fuel_revenue > 0 ? 'TOTAL TENANT VALUE' : 'TOTAL REVENUE'
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* 1. Total Yield */}
      <Card
        className={`hover:border-slate-400 cursor-pointer transition-colors shadow-sm ${activeStatus !== undefined ? 'ring-2 ring-slate-300' : ''}`}
        onClick={() => onStatusClick(undefined)}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">{yieldTitle}</CardTitle>
          <DollarSign className="h-4 w-4 text-emerald-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrency(displayedYield)}
            <span className="text-sm font-normal text-slate-500">/mo</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {breakdownParts.join(' · ')}
            {showFuel && data.fuel_revenue > 0 && <> · <span className="text-amber-600">Fuel: {formatCurrency(data.fuel_revenue)}</span></>}
            {isMtd && <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1 text-amber-600 border-amber-300">MTD</Badge>}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            ({formatCurrency(displayedYield * 12)}/yr annualized)
          </p>
        </CardContent>
      </Card>

      {/* 2. Occupancy */}
      <Card
        className={`hover:border-slate-400 cursor-pointer transition-colors shadow-sm ${activeStatus === 'idle' ? 'ring-2 ring-slate-400' : ''}`}
        onClick={() => onStatusClick(activeStatus === 'idle' ? undefined : 'idle')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">OCCUPANCY</CardTitle>
          <PieChart className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.occupancy_pct}%</div>
          <p className="text-xs text-slate-500 mt-1">
            {data.producing_sqft.toLocaleString()} / {data.total_sqft.toLocaleString()} sqft
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {data.producing_units} of {data.total_units} units
          </p>
        </CardContent>
      </Card>

      {/* 3. Opportunity */}
      <Card
        className={`hover:border-slate-400 cursor-pointer transition-colors shadow-sm ${activeStatus === 'underpriced' ? 'ring-2 ring-orange-400' : ''}`}
        onClick={() => onStatusClick(activeStatus === 'underpriced' ? undefined : 'underpriced')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">OPPORTUNITY</CardTitle>
          <Lightbulb className="h-4 w-4 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            Est. {formatCurrency(data.opportunity_monthly)}
            <span className="text-sm font-normal text-slate-500">/mo</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {data.idle_units} idle · {data.underpriced_units} underpriced
          </p>
          <p className="text-xs text-slate-400 mt-1">
            ({formatCurrency(data.opportunity_annualized)}/yr potential)
          </p>
        </CardContent>
      </Card>

      {/* 4. Expiring */}
      <Card
        className={`hover:border-slate-400 cursor-pointer transition-colors shadow-sm ${activeStatus === 'expiring' ? 'ring-2 ring-yellow-400' : ''}`}
        onClick={() => onStatusClick(activeStatus === 'expiring' ? undefined : 'expiring')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">EXPIRING (90 DAYS)</CardTitle>
          <AlertTriangle className="h-4 w-4 text-rose-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.expiring_count} fixed-term</div>
          <p className="text-xs text-slate-500 mt-1">
            {formatCurrency(data.expiring_risk)}/mo risk
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {data.m2m_count} month-to-month
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
